import { useCallback, useEffect, useMemo, useState, Fragment, useRef } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";

import { IoIosArrowBack } from "react-icons/io";
import { IoIosArrowForward } from "react-icons/io";
import { IoMdClose } from "react-icons/io";

import { GetFolder } from "../../../api";
import RefreshButton from "../Common/RefreshButton";
import * as Module from "../../../modules";
import UpdateDBButton from "../Common/UpdateDBButton";
import DateEditModal from "./ScrollDatePicker";

type FolderMap = Record<string, Record<string, string[]>>;

type DayButtonProps = {
    $isCurrentMonth: boolean;
    $isAvailable: boolean;
    $isSelected: boolean;
};

const MOBILE_BREAKPOINT = "768px";
const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

const DateInputBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    margin-top: 12px;
    gap: 24px;
`;

const CalendarContainer = styled.div`
    width: 100%;
    max-width: 560px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--text-color);
    border-radius: 18px;
    padding: 20px;
    background: var(--bg-color);
    box-sizing: border-box;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);

    @media (max-width: ${MOBILE_BREAKPOINT}) {
        padding: 16px;
        border-radius: 14px;
    }
`;

const CalendarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    & .closeBtn {
        transition: 200ms;
        &:hover {
            transform: translateY(-2px);
        }
    }
`;

const MonthLabel = styled.span`

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    & button {
        outline: none;
        border: none;
        background-color: transparent;
        font-size: 1.2rem;
        font-weight: 600;
        color: var(--text-color);
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const NavigationButton = styled.button`
    border: none;
    background: transparent;
    color: var(--text-color);
    cursor: pointer;
    font-size: 1.6rem;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 8px;

    &:hover {
        background: rgba(255, 255, 255, 0.06);
    }
`;

const TodayButton = styled.button`
    border: 1px solid var(--btn-bg-color);
    border-radius: 10px;
    background: transparent;
    color: var(--btn-bg-color);
    padding: 6px 14px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: var(--btn-bg-color);
        color: var(--bg-color);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.4;
        background: transparent;
        color: var(--text-color);
        border-color: rgba(255, 255, 255, 0.2);
    }
`;

const WeekDaysRow = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin: 14px 0 8px;
    font-size: 0.8rem;
    text-align: center;
    color: var(--text-color);
`;

const WeekdayCell = styled.div`
    padding: 4px 0;
    font-weight: 500;
`;

const CalendarGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 8px;

    @media (max-width: ${MOBILE_BREAKPOINT}) {
        gap: 6px;
    }
`;

const DayButton = styled.button<DayButtonProps>`
    position: relative;
    border: none;
    border-radius: 12px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    color: ${({ $isSelected }) => ($isSelected ? "var(--bg-color)" : "var(--text-color)")};
    background: ${({ $isSelected }) => ($isSelected ? "var(--btn-bg-color)" : "transparent")};
    opacity: ${({ $isCurrentMonth }) => ($isCurrentMonth ? 1 : 0.35)};
    cursor: ${({ $isAvailable }) => ($isAvailable ? "pointer" : "default")};
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;

    &:hover {
        ${({ $isAvailable, $isSelected }) =>
            $isAvailable && !$isSelected
                ? "background: rgba(255, 255, 255, 0.08); transform: translateY(-1px);"
                : ""}
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.2;
    }
`;

const AvailabilityDot = styled.span`
    position: absolute;
    top: 12px;
    right: 16px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent-color);
`;

const ActionButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
    width: 100%;

    @media (max-width: ${MOBILE_BREAKPOINT}) {
        flex-direction: column;
        align-items: stretch;
        button {
            width: 100%;
        }
    }
`;

const EmptyState = styled.div`
    margin-top: 24px;
    text-align: center;
    font-size: 0.85rem;
    opacity: 0.7;
`;

const padTwo = (value: string | number): string => String(value).padStart(2, "0");

const toDateFromIsoKey = (isoKey: string): Date => {
    const [year, month, day] = isoKey.split("-").map((value) => Number(value));
    return new Date(year, month - 1, day);
};

const DateInput = ({ onSearch, display, hideCalendar }: { onSearch: any, display: boolean, hideCalendar: any }) => {
    const dispatch = useDispatch();
    const [availableDateKeys, setAvailableDateKeys] = useState<string[]>([]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
    const [displayEditDateModal, setDisplayEditDateModal] = useState(false);

    const formatIsoKey = useCallback((date: Date) => {
        if(date === null) return "";
        return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(date.getDate())}`;
    }, []);

    const availableDateSet = useMemo(() => new Set(availableDateKeys), [availableDateKeys]);

    const hasAnyDates = availableDateKeys.length > 0;

    const selectedKey = useMemo(() => (selectedDate ? formatIsoKey(selectedDate) : null), [selectedDate, formatIsoKey]);

    const ICON_SIZE = "1.2rem";

    const formatFolderKey = useCallback((date: Date) => {
        if(date === null) return "";
        return `${date.getFullYear()}.${padTwo(date.getMonth() + 1)}.${padTwo(date.getDate())}`;
    }, []);

    const extractAvailableDates = useCallback((data: FolderMap) => {
        const keys: string[] = [];
        Object.entries(data || {}).forEach(([year, months]) => {
            Object.entries(months || {}).forEach(([month, days]) => {
                (days || []).forEach((day) => {
                    keys.push(`${year}-${padTwo(Number(month))}-${padTwo(Number(day))}`);
                });
            });
        });
        return Array.from(new Set(keys)).sort();
    }, []);

    useEffect(() => {
        dispatch(Module.startLoading());
        GetFolder((data: FolderMap) => {
            const keys = extractAvailableDates(data);
            setAvailableDateKeys(keys);

            if (keys.length > 0) {
                const latestDate = toDateFromIsoKey(keys[keys.length - 1]);
                setSelectedDate(latestDate);
                setCurrentMonth(new Date(latestDate.getFullYear(), latestDate.getMonth(), 1));
            } else {
                setSelectedDate(null);
            }

            dispatch(Module.endLoading());
        });
    }, [dispatch, extractAvailableDates]);

    useEffect(() => {
        if (!selectedDate || !display) return;
        onSearch("folder", formatFolderKey(selectedDate), 1);

        return hideCalendar();
    }, [selectedDate, formatFolderKey, onSearch]);

    const calendarWeeks = useMemo(() => {
        const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const start = new Date(startOfMonth);
        start.setDate(start.getDate() - start.getDay());

        const weeks: Date[][] = [];
        for (let week = 0; week < 6; week += 1) {
            const days: Date[] = [];
            for (let day = 0; day < 7; day += 1) {
                const current = new Date(start);
                current.setDate(start.getDate() + week * 7 + day);
                days.push(current);
            }
            weeks.push(days);
        }
        return weeks;
    }, [currentMonth]);

    const handleDayClick = useCallback(
        (day: Date) => {
            const isoKey = formatIsoKey(day);
            if (!availableDateSet.has(isoKey)) return;

            const nextDate = new Date(day.getFullYear(), day.getMonth(), day.getDate());
            setSelectedDate(nextDate);
            setCurrentMonth(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
        },
        [availableDateSet, formatIsoKey]
    );

    const goToPreviousMonth = useCallback(() => {
        setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }, []);

    const goToNextMonth = useCallback(() => {
        setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }, []);

    const goToSelectedMonth = useCallback((year: number, month: number) => {
        setCurrentMonth(new Date(year, month, 1));
        setDisplayEditDateModal(false);
    }, []);

    const handleGoToday = useCallback(() => {
        if (availableDateKeys.length === 0) return;
        const today = new Date();
        const targetTime = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

        let closestKey = availableDateKeys[0];
        let closestDiff = Infinity;

        availableDateKeys.forEach((key) => {
            const date = toDateFromIsoKey(key);
            const diff = Math.abs(date.getTime() - targetTime);
            if (diff < closestDiff) {
                closestDiff = diff;
                closestKey = key;
            }
        });

        const nearestDate = toDateFromIsoKey(closestKey);
        if(selectedDate !== nearestDate) {
            setSelectedDate(nearestDate);
            setCurrentMonth(new Date(nearestDate.getFullYear(), nearestDate.getMonth(), 1));
        }
    }, [availableDateKeys]);

    return (
        <DateInputBox className={`calendar ${display ? "display" : "hidden"}`}>
            {   
                displayEditDateModal && 
                <DateEditModal 
                    currentMonth={currentMonth} 
                    handleClick={goToSelectedMonth} />
            }
            <CalendarContainer>
                <CalendarHeader>
                    <MonthLabel>
                        <NavigationButton type="button" onClick={goToPreviousMonth} aria-label="이전 달">
                            <IoIosArrowBack size={ICON_SIZE} />
                        </NavigationButton>
                        <button onClick={() => setDisplayEditDateModal(!displayEditDateModal)}>
                            {currentMonth.getFullYear()}년 {padTwo(currentMonth.getMonth() + 1)}월
                        </button>
                        <NavigationButton type="button" onClick={goToNextMonth} aria-label="다음 달">
                            <IoIosArrowForward size={ICON_SIZE} />
                        </NavigationButton>
                    </MonthLabel>
                    <div style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "8px"}}>
                        <TodayButton type="button" onClick={handleGoToday} disabled={!hasAnyDates}>
                            최근
                        </TodayButton>
                        <IoMdClose className="closeBtn" style={{cursor: "pointer"}} size={ICON_SIZE} onClick={hideCalendar} />
                    </div>
                    
                </CalendarHeader>

                <WeekDaysRow>
                    {WEEKDAY_LABELS.map((label) => (
                        <WeekdayCell key={label}>{label}</WeekdayCell>
                    ))}
                </WeekDaysRow>
                <CalendarGrid>
                    {calendarWeeks.map((week, weekIndex) => (
                        <Fragment key={`week-${weekIndex}`}>
                            {week.map((day) => {
                                const isoKey = formatIsoKey(day);
                                const isAvailable = availableDateSet.has(isoKey);
                                const isSelected = selectedKey === isoKey;
                                const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

                                return (
                                    <DayButton
                                        key={isoKey}
                                        type="button"
                                        onClick={() => handleDayClick(day)}
                                        disabled={!isAvailable}
                                        aria-pressed={isSelected}
                                        aria-label={`${formatFolderKey(day)}${isAvailable ? " 재생 목록이 있습니다" : " 재생 목록이 없습니다"}`}
                                        $isAvailable={isAvailable}
                                        $isSelected={isSelected}
                                        $isCurrentMonth={isCurrentMonth}
                                    >
                                        <span>{day.getDate()}</span>
                                        {isAvailable && <AvailabilityDot aria-hidden />}
                                    </DayButton>
                                );
                            })}
                        </Fragment>
                    ))}
                </CalendarGrid>

                {!hasAnyDates && (
                    <EmptyState>등록된 플레이리스트가 없습니다.</EmptyState>
                )}

                {/* <ActionButtonGroup>
                    <span style={{color: "var(--gray-color)"}}>{formatFolderKey(selectedDate)}</span>
                    <UpdateDBButton />
                    <RefreshButton />
                </ActionButtonGroup> */}
            </CalendarContainer>
        </DateInputBox>
    );
};

export default DateInput;
